import { BaseComponent, projectGeographyKey, stampGridGeography } from "./base_component"
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { Input, Node, Output, Socket } from 'rete'
import { booleanDataSocket, categoricalDataSocket } from "../socket_types"
import { ProjectProperties } from "."
import { SelectControl } from "../controls/select"
import { maskFromExtentAndShape } from "../bounding_box"
import { retrieveModelDataWCS } from "../model_retrieval"
import { createXYZ } from "ol/tilegrid"
import { BooleanTileGrid, CategoricalTileGrid } from "../tile_grid"
import { TypedArray } from "d3"

export interface RBCSource {
    name: string
    source: string
}

export interface RBCOption {
    label: string
    value: number
}

// will contain all relevant properties for the component
export interface RasterBoolCatComponentProperties {
    // name of the component
    name: string
    // Sources for the raster data held on the GeoServer (ex: mapbiomas:mapbiomas_2020)
    sources: RBCSource[]
    // a label for the selection (ex. 'Year')
    sourceType?: string
    // if sources are combined, they will all merge into a single call, however, if sources are not combined a dropdown box will allow the user to decide which source to use
    combineSources?: boolean
    // options on how to read the raster (ex. 1 == Forest, 2 == Grassland, etc.)
    options: RBCOption[]
}

// using the selected source, builds a categorical tile grid
async function getRasterCategoricalData(source: string, compProps: RasterBoolCatComponentProperties, projectProps: ProjectProperties, options: RBCOption[]) : Promise<CategoricalTileGrid> {

  const mask = await maskFromExtentAndShape(projectProps)
  const tileGrid = createXYZ()
  const outputTileRange = tileGrid.getTileRangeForExtentAndZ(projectProps.extent, projectProps.zoom)

  const geotiff = await retrieveModelDataWCS(projectProps.extent, source, outputTileRange)

  const rasters = await geotiff.readRasters({ bbox: projectProps.extent, width: outputTileRange.getWidth(), height: outputTileRange.getHeight() })
  const image = await geotiff.getImage()

  const result = new CategoricalTileGrid(
    projectProps.zoom,
    outputTileRange.minX,
    outputTileRange.minY,
    outputTileRange.getWidth(),
    outputTileRange.getHeight()
  )

  const labels = new Map<number, number>()
  options.forEach((option, i) => {
    labels.set(option.value, i+1)
  })


  for (let i = 0; i < (rasters[0] as TypedArray).length; i++) {

    let x = (outputTileRange.minX + i % image.getWidth())
    let y = (outputTileRange.minY + Math.floor(i / image.getWidth()))

    const val = mask.get(x,y) && rasters[0][i] > 0 ? labels.get(rasters[0][i]) : 255

    result.set(x, y, val ?? 255)

  }

  const textLabels = new Map<number, string>()
  options.forEach((option, i) => {
    textLabels.set(i+1, option.label)
  })

  result.setLabels(textLabels)

  return result
}

// Generic component template for handling boolean/categorical datasets with a raster source (ex. UKCEH)
export class RasterBoolCatComponent extends BaseComponent {
    projectProps: ProjectProperties
    compProps: RasterBoolCatComponentProperties
    categoricalData: Map<string, CategoricalTileGrid>
    boolData: Map<string, BooleanTileGrid>

    constructor(projectProps: ProjectProperties, compProps: RasterBoolCatComponentProperties, category?: string){
        super(compProps.name)
        this.category = category ?? 'Inputs'

        this.projectProps = projectProps
        this.compProps = compProps
        this.categoricalData = new Map()
        this.boolData = new Map()
    }

    async builder(node: Node) {

        // if required, add a control to select the source
        if (!this.compProps.combineSources && this.compProps.sources.length > 1) {
            node.addControl(
                new SelectControl(
                    this.editor, 
                    "sourceId", 
                    () => this.compProps.sources.map((source, i) => {return {name: source.name, value: source.source, id: i}}), 
                    () => {}, 
                    this.compProps.sourceType ?? 'Source'
                )
            )
        }

        // Add outputs for each option
        this.compProps.options.forEach(option => {
            node.addOutput(new Output(option.label, option.label, booleanDataSocket))
        })

        // Combination of all options
        node.addOutput(new Output("All", "All", categoricalDataSocket))
    }


    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const source = node.data.sourceId as number ? this.compProps.sources[node.data.sourceId as number].source : this.compProps.sources[0].source
        const sourceName = node.data.sourceId as number ? this.compProps.sources[node.data.sourceId as number].name : this.compProps.sources[0].name
        const props = { ...this.projectProps, extent: this.projectProps.extent.slice() }
        const geography = projectGeographyKey(props)
        const cacheKey = `${geography}::${source}`

        if (!this.categoricalData.has(cacheKey)) {
            const grid = await getRasterCategoricalData(source, this.compProps, props, this.compProps.options)
            // The area changed while this coverage was downloading. Drop it so
            // the run for the new area cannot publish this grid.
            if (projectGeographyKey(this.projectProps) !== geography) return
            stampGridGeography(grid, geography)
            this.categoricalData.set(cacheKey, grid)
        }
        if (projectGeographyKey(this.projectProps) !== geography) return
        const categoricalData = this.categoricalData.get(cacheKey)!
        categoricalData.name = this.compProps.name + ' ' + (sourceName ?? '')

        this.compProps.options
            // only process options that have a connection
            .filter(opt => node.outputs[opt.label].connections.length > 0)
            .forEach((option, i) => {
                const boolKey = `${cacheKey}::${option.label}`
                const boolGrid = this.boolData.get(boolKey) ?? categoricalData.getBoolFromLabel(option.label)
                boolGrid.name = option.label
                stampGridGeography(boolGrid, geography)
                outputs[option.label] = boolGrid
                this.boolData.set(boolKey, boolGrid)
        })
        // add the all output, already processed so no need to check if it has a connection
        outputs['All'] = categoricalData
    }
}